import { apiService } from './apiService';
import { invalidateBootstrapLookups } from './bootstrapService';
import { lookupService } from './lookupService';
import type { Location as StockLocation } from '../types';

// Vendor interfaces
export interface Vendor {
  id: number;
  name: string;
  code: string;
  // Backend fields
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  payment_terms?: number;
  /** Opt-in TDS. Default false — existing vendors unchanged. */
  tds_applicable?: boolean;
  tds_rate?: number | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;

  // Legacy aliases (kept for backward compatibility in UI)
  contact_email?: string;
  contact_phone?: string;
}

// Product interfaces
export interface Product {
  id: number;
  name: string;
  code: string;
  /** Scanning / label barcode (may differ from material code) */
  barcode?: string | null;
  category_id?: number;
  category?: {
    id: number;
    name: string;
  };
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  // Additional properties for inventory management
  stock_level?: number;
  reorder_level?: number;
  minimum_stock_level?: number;
  maximum_stock_level?: number;
  cost_price?: number;
  selling_price?: number;
  wholesale_price?: number | null;
  pack_size?: number | null;
  carton_size?: number | null;
  pack_selling_price?: number | null;
  pack_wholesale_price?: number | null;
  carton_selling_price?: number | null;
  carton_wholesale_price?: number | null;
  bag_size?: number | null;
  bag_selling_price?: number | null;
  bag_wholesale_price?: number | null;
  gst_category?: string;
  gst_rate?: number;
  unit_of_measure?: string;
  description?: string;
  manufacture_date?: string | null;
  expiry_date?: string | null;
  expiry_alert_days?: number;
}

/** Row from GET /vendors/vendor-products-catalog (all suppliers). */
export interface VendorProductCatalogItem {
  id: number;
  /** Client-added row: company product exists but no vendor_product line links it yet. */
  is_company_master_row?: boolean;
  vendor: {
    id: number;
    name: string;
    code: string;
    email?: string | null;
    phone?: string | null;
    contact_person?: string | null;
  };
  name: string;
  description?: string | null;
  unit_of_measure?: string | null;
  product: {
    id: number;
    name: string;
    code: string;
    unit_of_measure?: string | null;
    category?: { id: number; name: string } | null;
  } | null;
  vendor_product_code?: string | null;
  vendor_price: number;
  lead_time_days: number;
  minimum_order_quantity: number;
  is_preferred: boolean;
  created_at?: string;
}

/** Vendor catalog line (supplier item); optional link to company Product. */
export interface VendorProductLink {
  id: number;
  vendor_id: number;
  product_id?: number | null;
  name: string;
  description?: string | null;
  unit_of_measure?: string | null;
  vendor_product_code?: string | null;
  vendor_price: number;
  lead_time_days: number;
  minimum_order_quantity: number;
  is_preferred: boolean;
  created_at?: string;
  updated_at?: string;
  product?: Product | null;
}

// Category interfaces
export interface Category {
  id: number;
  name: string;
  description?: string;
  parent_id?: number | null;
  parent?: Category | null;
  children?: Category[] | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Location interfaces (assuming you have locations API)
export interface Location {
  id: number;
  name: string;
  code?: string;
  address?: string;
  phone?: string;
  email?: string;
  is_warehouse?: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export const masterDataService = {
  // Vendors
  async getVendors(params?: {
    skip?: number;
    limit?: number;
    active_only?: boolean;
    search?: string;
    forceRefresh?: boolean;
  }): Promise<Vendor[]> {
    return lookupService.getVendors(params);
  },

  async getVendor(id: number): Promise<Vendor> {
    return apiService.get<Vendor>(`/vendors/${id}`);
  },

  async createVendor(data: Omit<Vendor, 'id' | 'created_at' | 'updated_at'>): Promise<Vendor> {
    const created = await apiService.post<Vendor>('/vendors/', data);
    invalidateBootstrapLookups();
    return created;
  },

  async updateVendor(id: number, data: Partial<Vendor>): Promise<Vendor> {
    const updated = await apiService.put<Vendor>(`/vendors/${id}`, data);
    invalidateBootstrapLookups();
    return updated;
  },

  /** Permanently deletes the vendor (blocked if purchase orders reference them). */
  async deleteVendor(id: number): Promise<{ message: string }> {
    const result = await apiService.delete<{ message: string }>(`/vendors/${id}`);
    invalidateBootstrapLookups();
    return result;
  },

  async getVendorProductsCatalog(params?: {
    skip?: number;
    limit?: number;
    vendor_id?: number;
    product_id?: number;
    active_only?: boolean;
  }): Promise<{ items: VendorProductCatalogItem[]; total: number; skip: number; limit: number }> {
    // Primary: /vendors/vendor-products-catalog (auth + permissions aligned with vendor APIs)
    return apiService.get('/vendors/vendor-products-catalog', params);
  },

  async getVendorProducts(
    vendorId: number,
    params?: { skip?: number; limit?: number },
  ): Promise<VendorProductLink[]> {
    return apiService.get<VendorProductLink[]>(`/vendors/${vendorId}/products`, params);
  },

  async addVendorProduct(
    vendorId: number,
    p: {
      name: string;
      vendor_product_code?: string;
      vendor_price?: number;
      lead_time_days?: number;
      minimum_order_quantity?: number;
      is_preferred?: boolean;
      product_id?: number | null;
      description?: string | null;
      unit_of_measure?: string | null;
    },
  ): Promise<VendorProductLink> {
    const row = await apiService.post<VendorProductLink>(`/vendors/${vendorId}/products`, {
      name: p.name.trim(),
      vendor_product_code: p.vendor_product_code?.trim() || undefined,
      vendor_price: p.vendor_price ?? 0,
      lead_time_days: p.lead_time_days ?? 7,
      minimum_order_quantity: p.minimum_order_quantity ?? 1,
      is_preferred: p.is_preferred ?? false,
      product_id: p.product_id ?? undefined,
      description: p.description ?? undefined,
      unit_of_measure: p.unit_of_measure?.trim() || undefined,
    });
    invalidateBootstrapLookups();
    return row;
  },

  async updateVendorProduct(
    vendorId: number,
    vendorProductId: number,
    p: {
      name?: string;
      vendor_product_code?: string | null;
      vendor_price?: number;
      lead_time_days?: number;
      minimum_order_quantity?: number;
      is_preferred?: boolean;
      product_id?: number | null;
      description?: string | null;
      unit_of_measure?: string | null;
    },
  ): Promise<VendorProductLink> {
    const updated = await apiService.put<VendorProductLink>(
      `/vendors/${vendorId}/vendor-products/${vendorProductId}`,
      p,
    );
    invalidateBootstrapLookups();
    return updated;
  },

  async removeVendorProduct(vendorId: number, vendorProductId: number): Promise<{ message: string }> {
    const result = await apiService.delete<{ message: string }>(
      `/vendors/${vendorId}/vendor-products/${vendorProductId}`,
    );
    invalidateBootstrapLookups();
    return result;
  },

  // Products
  async getProducts(params?: {
    skip?: number;
    limit?: number;
    active_only?: boolean;
    search?: string;
    category_id?: number;
  }): Promise<Product[]> {
    return apiService.get<Product[]>('/products/', params);
  },

  async getProduct(id: number): Promise<Product> {
    return apiService.get<Product>(`/products/${id}`);
  },

  async createProduct(productData: any): Promise<Product> {
    return apiService.post<Product>('/products/', productData);
  },

  async updateProduct(id: number, productData: any): Promise<Product> {
    return apiService.put<Product>(`/products/${id}`, productData);
  },

  async deleteProduct(id: number): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/products/${id}`);
  },

  // Categories
  async getCategories(params?: {
    skip?: number;
    limit?: number;
    active_only?: boolean;
    forceRefresh?: boolean;
  }): Promise<Category[]> {
    return lookupService.getCategories(params);
  },

  async getCategory(id: number): Promise<Category> {
    return apiService.get<Category>(`/products/categories/${id}`);
  },

  async createCategory(data: {
    name: string;
    description?: string;
    parent_id?: number | null;
    is_active?: boolean;
  }): Promise<Category> {
    const created = await apiService.post<Category>('/category/', data);
    invalidateBootstrapLookups();
    return created;
  },

  // Locations
  async getLocations(params?: { skip?: number; limit?: number; active_only?: boolean; is_active?: boolean; search?: string; is_warehouse?: boolean; forceRefresh?: boolean }): Promise<StockLocation[]> {
    return lookupService.getLocations(params);
  },

  async getLocation(id: number): Promise<Location> {
    return apiService.get<Location>(`/locations/${id}`);
  }
};

export default masterDataService;